import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { createId } from '@core/util/file';
import * as actions from './actions';
import { TeranetDocket, MatterLRO } from '@models/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { UserProfileState } from '@state/user-profile';
import { TeranetService } from '@services/teranet/teranet.service';
import {
	Company,
	Execution,
	Person,
	SavedWrit,
	Writ,
	WritExecution,
	WritName,
	WritSearchItem,
	WritSearchStatus,
	WritSearchType,
} from '@models/teranet';
import { PurchaseStates, PurchaseStateType } from '@state/utils/models';
import LogRocket from 'logrocket';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { LROData } from '../../data/LRO';
import { Clients, MatterClients } from '@state/global/matter_clients';
import { isGuarantor } from '@core/util/client';
import { CollectionPaths } from '@models/collections';
import {
	MatterWithSubscriptionStateModel,
	FirestoreStateBaseForMatterWithSubscription,
} from '@state/FirestoreStateBaseForMatter';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';
import { patch, removeItem } from '@ngxs/store/operators';
import { firstValueFrom, Observable } from 'rxjs';
import { ToastService } from 'src/app/ui/toast/toast.service';

interface SaleWritsStateModel extends MatterWithSubscriptionStateModel {
	id?: string;
	searchItems: WritSearchItem[];
	savedItems: SavedWrit[];
	executionPurchaseStates: PurchaseStates;
	loading?: boolean;
}
type Ctx = StateContext<SaleWritsStateModel>;

const DEFAULT_STATE: SaleWritsStateModel = {
	...DEFAULT_STATE_BASE,
	searchItems: [],
	savedItems: [],
	executionPurchaseStates: {},
	loading: false,
};

@State<SaleWritsStateModel>({
	name: 'saleWrits',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class SaleWritState extends FirestoreStateBaseForMatterWithSubscription<
	SavedWrit,
	SaleWritsStateModel
> {
	@SelectSnapshot(UserProfileState.getTeranetUsername) teranetUsername;
	@SelectSnapshot(UserProfileState.getTeranetPassword) teranetPassword;
	@SelectSnapshot(UserProfileState.getTeranetAccountName) teranetAccount;
	@SelectSnapshot(SelectedMatter.teranetDocket) docket: TeranetDocket;
	@SelectSnapshot(SelectedMatter.LRO) lro: MatterLRO;

	constructor(
		private teranetService: TeranetService,
		private toastService: ToastService,
	) {
		super(
			({ businessId, matterId }) =>
				CollectionPaths.business_matter_writs(businessId, matterId),
			(doc) => ({ savedItems: doc as SavedWrit[] }),
			() => DEFAULT_STATE,
		);
	}

	@Selector([SaleWritState])
	static executionPurchaseStates(state: SaleWritsStateModel) {
		return state?.executionPurchaseStates;
	}

	@Selector([SaleWritState])
	static searchItems(state: SaleWritsStateModel): WritSearchItem[] {
		return state?.searchItems;
	}

	@Selector([SaleWritState])
	static savedItems(state: SaleWritsStateModel): SavedWrit[] {
		return state?.savedItems;
	}

	@Selector([SaleWritState.savedItems])
	static hasSavedWrits(items: SavedWrit[]): boolean {
		return !!items?.length;
	}

	@Selector([SaleWritState])
	static loading(state: SaleWritsStateModel): boolean {
		return state?.loading;
	}

	@Selector([
		MatterClients.get,
		SaleWritState.searchItems,
		SaleWritState.savedItems,
	])
	static allClientsAvailableToAdd(
		clients: Clients[],
		searchItems: WritSearchItem[],
		savedItems: SavedWrit[],
	): Clients[] | null {
		let returnClients: Clients[] = clients;
		if (clients?.length) {
			if (savedItems?.length || searchItems.length) {
				const unselected = clients.filter(
					(client) =>
						!(
							savedItems?.some((saved) => saved?.id === client?.id) ||
							searchItems?.some((search) => search?.item?.id === client?.id)
						),
				);

				returnClients = unselected.length ? unselected : null;
			}
		}

		return returnClients?.sort((a, b) => {
			const isAGuarantor = isGuarantor(a);
			const isBGuarantor = isGuarantor(b);

			return isAGuarantor && isBGuarantor ? 0 : isAGuarantor ? 1 : -1;
		});
	}

	@Selector([
		SaleWritState.allClientsAvailableToAdd,
		SaleWritState.searchItems,
		SaleWritState.savedItems,
	])
	static eligbleAutoAddClients(
		clients: Clients[],
		searchItems: WritSearchItem[],
		savedItems: SavedWrit[],
	): Clients[] {
		if (!(searchItems?.length || savedItems?.length) && clients?.length) {
			return clients.filter((client) => !isGuarantor(client));
		}

		return [];
	}

	@Selector([SaleWritState.savedItems])
	static hasExecutions(savedItems: SavedWrit[]): boolean {
		return savedItems?.some((item) =>
			item?.writ?.names
				? Object.values(item?.writ?.names)?.some((name) => name?.execution)
				: false,
		);
	}

	@Action(actions.RemoveSearchItem)
	removeSearchWrit({ setState }: Ctx, { index }: actions.RemoveSearchItem) {
		setState(patch({ searchItems: removeItem(index) }));
	}

	@Action(actions.PurchaseWrit)
	async purchaseWrit(
		{ getState, dispatch }: Ctx,
		{ writId }: actions.PurchaseWrit,
	) {
		await this.repo.CreateOrMerge(writId, {
			status: WritSearchStatus.SEARCHING,
		});

		const state = getState();
		const item = state.savedItems.find((x) => x.id === writId);

		if (item.search.type === WritSearchType.PERSON) {
			dispatch(
				new actions.SearchPersonItem({
					writId,
					person: item.search.item as Person,
				}),
			);
		} else if (item.search.type === WritSearchType.COMPANY) {
			dispatch(
				new actions.SearchCompanyItem({
					writId,
					company: item.search.item as Company,
				}),
			);
		} else {
			dispatch(
				new actions.SearchExecutionItem({
					writId,
					execution: item.search.item as Execution,
				}),
			);
		}
	}

	@Action(actions.SearchWrits)
	async searchWrits(
		{ dispatch, patchState }: Ctx,
		{ searches }: actions.SearchWrits,
	) {
		try {
			patchState({ loading: true });

			for (const item of searches) {
				const uid = createId();
				try {
					const _writ: SavedWrit = {
						id: uid,
						search: item,
						status: WritSearchStatus.FAILED,
					};

					await this.repo.CreateOrMerge(uid, _writ);

					let request: Observable<void>;

					if (item.type === WritSearchType.PERSON) {
						request = dispatch(
							new actions.SearchPersonItem({
								writId: uid,
								person: item.item as Person,
							}),
						);
					} else if (item.type === WritSearchType.COMPANY) {
						request = dispatch(
							new actions.SearchCompanyItem({
								writId: uid,
								company: item.item as Company,
							}),
						);
					} else {
						request = dispatch(
							new actions.SearchExecutionItem({
								writId: uid,
								execution: item.item as Execution,
							}),
						);
					}

					await firstValueFrom(request);
				} catch (err) {
					LogRocket.log('Writ Search', 'Error initializing Search', err);
					throw err;
				}
			}
		} catch (err) {
			LogRocket.log('Error searching for writ', err);
		} finally {
			patchState({ searchItems: [] });
			patchState({ loading: false });
		}
	}

	@Action(actions.PurchaseExecution)
	async purchaseExecution(
		{ patchState, getState, dispatch }: Ctx,
		{ payload }: actions.PurchaseExecution,
	) {
		const _state = getState();
		patchState({
			executionPurchaseStates: {
				..._state.executionPurchaseStates,
				[payload.executionNumber]: { state: PurchaseStateType.PURCHASING },
			},
		});

		try {
			const _lro = LROData[this.lro.selected.id];
			const result = (await firstValueFrom(
				this.teranetService.getWritDetail({
					docket_system_id: this.docket.docketTeranetSystemId,
					lro: this.lro.selected.id,
					enforcement_office_number: _lro.EO,
					file_number: payload.executionNumber,
				}),
			)) as WritName;

			const writ = _state.savedItems.find((x) => x.id === payload.writId);

			if (writ?.writ) {
				await this.repo.CreateOrMerge(payload.writId, {
					// @ts-expect-errors - This is legacy code that needs to be refactored the typing is completely off
					writ: { names: { [payload.executionNumber]: { execution: result } } },
				});
				dispatch(new actions.PurchaseExecutionSuccess(payload.executionNumber));
			} else {
				throw new Error(
					`Unable to find writ for saleId:${this.matterId}, writ:${payload.writId}, execution number: ${payload.executionNumber}`,
				);
			}
		} catch (err) {
			dispatch(
				new actions.PurchaseExecutionFailure({
					executionNumber: payload.executionNumber,
					error: err,
				}),
			);
		}
	}

	@Action(actions.PurchaseExecutionSuccess)
	purchaseExecutionSuccess(
		{ patchState, getState }: Ctx,
		{ executionNumber }: actions.PurchaseExecutionSuccess,
	) {
		const _state = getState();
		patchState({
			executionPurchaseStates: {
				..._state.executionPurchaseStates,
				[executionNumber]: { state: PurchaseStateType.SUCCESSFUL },
			},
		});
	}

	@Action(actions.PurchaseExecutionFailure)
	purchaseExecutionFailure(
		{ patchState, getState }: Ctx,
		{ payload }: actions.PurchaseExecutionFailure,
	) {
		const _state = getState();
		const friendlyError = this.teranetService.HandleError(
			'Unable to purchase execution',
			payload.error,
		);
		patchState({
			executionPurchaseStates: {
				..._state.executionPurchaseStates,
				[payload.executionNumber]: {
					state: PurchaseStateType.ERROR,
					error: friendlyError,
				},
			},
		});
	}

	@Action(actions.SearchPersonItem)
	async searchPerson(
		ctx: Ctx,
		{ payload }: actions.SearchPersonItem,
	): Promise<void> {
		try {
			const _lro = LROData[this.lro.selected.id];
			const result = await firstValueFrom(
				this.teranetService.searchWritByName({
					clientId: payload.person.id,
					docket_system_id: this.docket.docketTeranetSystemId,
					first_name: payload.person.firstName,
					last_name: payload.person.lastName,
					lro: this.lro.selected.id,
					enforcement_office_number: _lro.EO,
					type: 'P',
				}),
			);
			result.names =
				!result?.names || result?.names?.length === 0
					? {}
					: result?.names?.reduce((obj, name) => {
							obj[name.execution_number] = name;

							return obj;
						}, {});

			await this.repo.CreateOrMerge(payload.writId, {
				writ: result,
				status: WritSearchStatus.SUCCESS,
			});

			this.showWritSearchSuccessToast();
		} catch (err) {
			LogRocket.error('Search_Writ_By_Person', err);
			const friendlyError = this.teranetService.HandleError(
				'Error searching for Writ',
				err,
			);
			await this.repo.CreateOrMerge(payload.writId, {
				status: WritSearchStatus.FAILED,
				error: friendlyError,
			});
			throw err;
		}
	}

	@Action(actions.SearchCompanyItem)
	async searchCompany(ctx: Ctx, { payload }: actions.SearchCompanyItem) {
		try {
			const _lro = LROData[this.lro.selected.id];
			const result = await firstValueFrom(
				this.teranetService.searchWritByName({
					clientId: payload.company.id,
					docket_system_id: this.docket.docketTeranetSystemId,
					first_name: payload.company.name,
					last_name: '',
					lro: this.lro.selected.id,
					enforcement_office_number: _lro.EO,
					type: 'C',
				}),
			);
			result.names =
				!result?.names || result?.names?.length === 0
					? {}
					: result?.names?.reduce((obj, name) => {
							obj[name.execution_number] = name;

							return obj;
						}, {});

			await this.repo.CreateOrMerge(payload.writId, {
				writ: result,
				status: WritSearchStatus.SUCCESS,
			});

			this.showWritSearchSuccessToast();
		} catch (err) {
			LogRocket.error('Search_Writ_By_Company_Name', err);
			const friendlyError = this.teranetService.HandleError(
				'Error searching for Writ',
				err,
			);
			await this.repo.CreateOrMerge(payload.writId, {
				status: WritSearchStatus.FAILED,
				error: friendlyError,
			});
			throw err;
		}
	}

	@Action(actions.SearchExecutionItem)
	async searchExecution(ctx: Ctx, { payload }: actions.SearchExecutionItem) {
		try {
			const _lro = LROData[this.lro.selected.id];
			const result = (await firstValueFrom(
				this.teranetService.getWritDetail({
					docket_system_id: this.docket.docketTeranetSystemId,
					lro: this.lro.selected.id,
					enforcement_office_number: _lro.EO,
					file_number: payload.execution.number,
				}),
			)) as WritExecution;

			const writ: Writ = {
				certificate: result.certificate,
				names: {
					[payload.execution.number]: {
						first_name: '',
						last_name: '',
						execution_number: payload.execution.number,
						execution: result,
					},
				},
				price: result.price,
				product_name: result.product_name,
			};
			await this.repo.CreateOrMerge(payload.writId, {
				writ: writ,
				status: WritSearchStatus.SUCCESS,
			});

			this.showWritSearchSuccessToast();
		} catch (err) {
			LogRocket.error('Search_Writ_By_Execution', err);
			const friendlyError = this.teranetService.HandleError(
				'Error searching for Writ',
				err,
			);
			await this.repo.CreateOrMerge(payload.writId, {
				status: WritSearchStatus.FAILED,
				error: friendlyError,
			});
			throw err;
		}
	}

	private showWritSearchSuccessToast(): void {
		this.toastService.success('Writ search purchased successfully!', 4000, {
			vertical: 'bottom',
			horizontal: 'center',
		});
	}
}
